// A find/replace pair applied to every string before it leaves the client.
export interface RedactionRule {
  pattern: RegExp;
  replacement: string;
}

// OTLP / Faro identity fields — never phone-scrub these values. Hex IDs are
// 16/32 chars of `[0-9a-f]`; an 8-digit run bounded by hex letters looks like a
// phone to a digit-only lookaround and becomes `[redacted-phone]`, which the
// Faro collector rejects (`ID.UnmarshalJSONIter: length mismatch` → 400).
const OTEL_ID_KEYS = new Set(['traceId', 'spanId', 'parentSpanId', 'trace_id', 'span_id', 'parent_span_id']);

// Defaults: emails and Norwegian phone numbers. Apps with other PII shapes pass
// their own rules to `createPiiScrubber`.
// Boundaries use `[0-9a-fA-F]` (not just `\d`) so:
// - 8-digit runs inside longer numbers stay intact (`startTimeUnixNano`)
// - 8-digit runs inside hex `traceId`/`spanId` stay intact
// Without this, Faro returns 400 and the SDK retries (network-tab spam).
export const DEFAULT_REDACTION_RULES: RedactionRule[] = [
  { pattern: /[\w.+-]+@[\w-]+\.[\w.-]+/g, replacement: '[redacted-email]' },
  { pattern: /(?<![0-9a-fA-F])(\+?47)?\s?\d{8}(?![0-9a-fA-F])/g, replacement: '[redacted-phone]' }
];

type ScrubbableItem<TPayload = unknown> = { payload: TPayload };

const redactString = (value: string, rules: RedactionRule[]): string =>
  rules.reduce((acc, { pattern, replacement }) => acc.replace(pattern, replacement), value);

const scrubValue = (value: unknown, rules: RedactionRule[]): unknown => {
  if (typeof value === 'string') {
    return redactString(value, rules);
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, rules));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, OTEL_ID_KEYS.has(key) ? val : scrubValue(val, rules)])
    );
  }
  return value;
};

// Builds a `beforeSend` hook that recursively redacts PII from every string
// in a transport item's payload (event attributes, log messages, URLs, …).
export const createPiiScrubber =
  (rules: RedactionRule[] = DEFAULT_REDACTION_RULES) =>
  <T extends ScrubbableItem>(item: T): T => ({
    ...item,
    payload: scrubValue(item.payload, rules)
  });

// Default scrubber using the built-in email + Norwegian-phone rules.
export const scrubPII = createPiiScrubber();
